import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, Building2, Eye, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { PaginationBar, EmptyState, LoadingBlock } from "@/components/Pagination";
import { RowActionsMenu } from "@/components/admin/RowActionsMenu";
import { StatusFilterTabs } from "@/components/admin/StatusFilterTabs";
import { useLifecycleActions } from "@/components/admin/useLifecycleActions";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ORG_TYPES = [
  "hospital", "medical_college", "nursing_institute", "diagnostic_center",
  "healthcare_training_institute", "corporate_healthcare_organization", "individual_learner",
];

const PAGE_SIZE = 15;

export default function AdminOrganizationsSummaryPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, status_filter: statusFilter };
      if (search) params.search = search;
      if (type !== "all") params.org_type = type;
      if (status !== "all") params.status = status;
      const { data } = await api.get("/admin/organizations-summary", { params });
      setRows(data.data.organizations);
      setTotal(data.data.total);
    } finally {
      setLoading(false);
    }
  }, [page, search, type, status, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const lc = useLifecycleActions({
    basePath: "/admin/organizations",
    labelOf: (row) => `"${row.organization_name}"`,
    onDone: load,
  });

  return (
    <DashboardLayout title="Organizations" breadcrumbs={["Platform", "Organizations"]}>
      <div className="space-y-4" data-testid={ADMIN.orgsRoot}>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-3">
            <StatusFilterTabs value={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(1); }} testId="orgs-status-filter" />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" placeholder="Search by name, code or city" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid={ADMIN.orgsSearch} />
            </div>
            <Select value={type} onValueChange={(v) => { setType(v); setPage(1); }}>
              <SelectTrigger className="w-56" data-testid={ADMIN.orgsTypeFilter}><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {ORG_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
              <SelectTrigger className="w-40" data-testid={ADMIN.orgsStatusFilter}><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => navigate("/app/super-admin/organizations/new")}
                className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]"
                data-testid="admin-org-add-btn"
              >
                <ExternalLink className="h-4 w-4 rotate-45" /> Add organization
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white">
          {loading ? (
            <LoadingBlock label="Loading organizations…" />
          ) : rows.length === 0 ? (
            <EmptyState title="No organizations found" description="Try clearing filters." icon={Building2} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Organization</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Location</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Users</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Plan · Days left</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Created</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((o) => (
                  <TableRow key={o.id} data-testid={ADMIN.orgRow(o.id)}>
                    <TableCell>
                      <div className="text-sm font-semibold text-slate-900">{o.organization_name}</div>
                      <div className="text-xs text-slate-500">{o.organization_code}</div>
                    </TableCell>
                    <TableCell><span className="text-xs uppercase tracking-widest text-slate-600">{(o.organization_type || "").replace(/_/g, " ")}</span></TableCell>
                    <TableCell className="text-sm text-slate-700">{o.city}, {o.state}</TableCell>
                    <TableCell className="text-sm font-semibold text-slate-900">{o.user_count}</TableCell>
                    <TableCell>
                      {o.subscription_plan_name ? (() => {
                        const eff = o.subscription_effective_status || "none";
                        const days = o.subscription_days_remaining;
                        const tone = eff === "active" ? "text-emerald-700 border-emerald-200 bg-emerald-50"
                          : eff === "in_grace" ? "text-amber-700 border-amber-200 bg-amber-50"
                          : eff === "expired" ? "text-rose-700 border-rose-200 bg-rose-50"
                          : "text-slate-600 border-slate-200 bg-slate-50";
                        return (
                          <div className="space-y-0.5" data-testid={`org-sub-${o.id}`}>
                            <div className="text-sm font-semibold text-slate-900">{o.subscription_plan_name}</div>
                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${tone}`}>
                              {eff === "in_grace" ? "IN GRACE" : eff}{(eff === "active" || eff === "in_grace") && ` · ${days}d left`}
                            </span>
                          </div>
                        );
                      })() : (
                        <span className="text-xs text-slate-400 italic" data-testid={`org-sub-none-${o.id}`}>No plan</span>
                      )}
                    </TableCell>
                    <TableCell><StatusBadge status={o.deleted_at ? "deleted" : o.status} /></TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1 justify-end">
                        <Link to={`/app/super-admin/organizations/${o.id}`} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]">
                          <Eye className="h-3 w-3" /> View
                        </Link>
                        <RowActionsMenu
                          testId={`org-actions-${o.id}`}
                          status={o.deleted_at ? "deleted" : o.status}
                          onView={() => navigate(`/app/super-admin/organizations/${o.id}`)}
                          onEdit={() => navigate(`/app/super-admin/organizations/${o.id}`)}
                          onDeactivate={() => lc.open("deactivate", o)}
                          onReactivate={() => lc.open("reactivate", o)}
                          onDelete={() => lc.open("delete", o)}
                          onRestore={() => lc.open("restore", o)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <PaginationBar page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testKey="orgs-pagination" />
        </div>
      </div>
      {lc.dialog}
    </DashboardLayout>
  );
}
